import { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { isPlayerLobbyPath, isTransportFailure } from '../services/playerConnection';
import {
  clearStoredSessionToken,
  currentPortalFromPath,
  getStoredSessionToken,
  storeSessionToken,
} from '../services/sessionTokens';
import { tenantSlugFromHost, tenantSlugFromPathOrHost } from '../utils/tenantDomains';

const AuthContext = createContext(null);
const LOCAL_DEMO_TOKEN = 'demo_staff_chat';
const LOCAL_PLAYER_DEMO_TOKEN = 'demo_player_chat';
const LOCAL_DEMO_EMAIL = 'demo@white.room';
const LOCAL_PLAYER_DEMO_EMAIL = 'player@white.room';
const LOCAL_DEMO_PASSWORD = 'DemoChat2026!';
const LOCAL_DEMO_PASSWORD_SHORT = 'demo';

const isLocalDemoHost = () => (
  typeof window !== 'undefined'
  && ['127.0.0.1', 'localhost'].includes(window.location.hostname)
);

const setDemoSessionToken = (token) => {
  storeSessionToken(token, { tenant: 'demo', portal: currentPortalFromPath(), sessionOnly: true });
};

const localDemoUser = {
  id: 'demo-staff-chat',
  username: 'White Room Demo',
  email: LOCAL_DEMO_EMAIL,
  role: 'admin',
  tenant_id: 'demo-tenant',
  tenant_slug: 'demo',
  tenant_name: 'White Room Demo',
  is_master_admin: true,
  permissions: ['chat:respond'],
};

const localPlayerDemoUser = {
  id: 'demo-player-1',
  username: 'Emily Demo',
  email: LOCAL_PLAYER_DEMO_EMAIL,
  role: 'player',
  tenant_id: 'demo-tenant',
  tenant_slug: 'demo',
  tenant_name: 'White Room Demo',
  vip_rewards_enabled: true,
  phone_verified: false,
  phoneVerified: false,
  phone_verify_reward_enabled: true,
  phone_verify_reward_fp: 5,
  phone_verify_reward_source: 'demo',
  balance: 52,
};

const ADMIN_PORTAL_ROLES = new Set([
  'admin',
  'tenant_admin',
  'moderator',
  'support',
  'staff',
  'support_agent',
  'finance_staff',
  'survey_staff',
  'agent',
  'affiliate_manager',
  'referral_agent',
]);

export const authPortalForUser = (authUser) => ADMIN_PORTAL_ROLES.has(authUser?.role) ? 'admin' : 'player';

const normalizedPhoneVerified = (authUser) => {
  if (typeof authUser?.phone_verified === 'boolean') return authUser.phone_verified;
  if (typeof authUser?.phoneVerified === 'boolean') return authUser.phoneVerified;
  return Boolean(authUser?.phone_verified_at);
};

const normalizeAuthUserPhoneVerification = (authUser) => {
  if (!authUser) return authUser;
  const hasPhoneState = (
    typeof authUser.phone_verified === 'boolean'
    || typeof authUser.phoneVerified === 'boolean'
    || Boolean(authUser.phone_verified_at)
  );
  if (!hasPhoneState) return authUser;
  const verified = normalizedPhoneVerified(authUser);
  return { ...authUser, phone_verified: verified, phoneVerified: verified };
};

const stampPlayerRewardsBootstrap = (authUser) => {
  if (!authUser) return authUser;
  const normalizedUser = normalizeAuthUserPhoneVerification(authUser);
  const receivedAt = Date.now();
  const bootstrapFields = {};
  if (typeof normalizedUser.vip_rewards_enabled === 'boolean') {
    bootstrapFields.vip_rewards_bootstrap_received_at = receivedAt;
  }
  if (typeof normalizedUser.phone_verify_reward_enabled === 'boolean') {
    bootstrapFields.phone_reward_bootstrap_received_at = receivedAt;
  }
  return Object.keys(bootstrapFields).length > 0
    ? { ...normalizedUser, ...bootstrapFields }
    : normalizedUser;
};

const hydratePhoneRewardBootstrap = async (authUser) => {
  const normalizedUser = normalizeAuthUserPhoneVerification(authUser);
  const role = String(normalizedUser?.role || 'user').trim().toLowerCase();
  if (
    !normalizedUser
    || normalizedUser.is_master_admin
    || ADMIN_PORTAL_ROLES.has(role)
    || normalizedPhoneVerified(normalizedUser)
    || typeof normalizedUser.phone_verify_reward_enabled === 'boolean'
  ) {
    return normalizedUser;
  }

  try {
    const response = await api.get(
      '/api/users/me/phone-verification/status',
      { timeout: 1500 },
    );
    const status = response.data || {};
    const verified = status.verified === true;
    const rewardResolved = (
      status.phone_verify_reward_resolved !== false
      && Boolean(status.phone_verify_reward_source)
    );
    return {
      ...normalizedUser,
      phone_verified: verified,
      phoneVerified: verified,
      ...(rewardResolved ? {
        phone_verify_reward_enabled: status.phone_verify_reward_enabled === true,
        phone_verify_reward_fp: Number(status.phone_verify_reward_fp || 0),
        phone_verify_reward_source: status.phone_verify_reward_source,
      } : {}),
    };
  } catch {
    return normalizedUser;
  }
};

const resolveTenantSlug = () => {
  return tenantSlugFromPathOrHost();
};

const normalizeTenantKey = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .replace(/\/.*$/, '')
  .replace(/\.$/, '');

const addTenantAlias = (aliases, value) => {
  const normalized = normalizeTenantKey(value);
  if (!normalized) return;
  aliases.add(normalized);
  if (!normalized.includes('.')) aliases.add(`${normalized}.playemp.vip`);
};

const collectTenantAliases = (authUser = {}) => {
  const aliases = new Set();
  [
    authUser.tenant_slug,
    authUser.tenant_domain,
    authUser.tenant_custom_domain,
    authUser.custom_domain,
    authUser.customDomain,
    authUser.canonical_domain,
    authUser.canonicalDomain,
    authUser.domain,
    authUser.host,
    authUser.tenant?.slug,
    authUser.tenant?.custom_domain,
    authUser.tenant?.customDomain,
    authUser.tenant?.canonical_domain,
  ].forEach((value) => addTenantAlias(aliases, value));

  [
    authUser.tenant_domains,
    authUser.tenant_domain_aliases,
    authUser.custom_domains,
    authUser.domains,
    authUser.domain_aliases,
    authUser.tenant?.custom_domains,
    authUser.tenant?.domains,
    authUser.tenant?.domain_aliases,
  ].forEach((values) => {
    if (Array.isArray(values)) values.forEach((value) => addTenantAlias(aliases, value));
  });
  return aliases;
};

const sessionMatchesCurrentTenant = (authUser, currentTenant) => {
  const current = normalizeTenantKey(currentTenant);
  if (!current || !authUser?.tenant_id) return true;
  const allowed = collectTenantAliases(authUser);
  return allowed.has(current);
};

const storeSessionForTenantAliases = (token, {
  canonicalTenant,
  currentTenant,
  portal,
  sessionOnly = false,
} = {}) => {
  if (!token) return;
  const tenants = new Set();
  const canonical = normalizeTenantKey(canonicalTenant);
  const current = normalizeTenantKey(currentTenant);
  const hostTenant = normalizeTenantKey(tenantSlugFromHost());
  if (canonical) tenants.add(canonical);
  if (current) tenants.add(current);
  if (hostTenant) tenants.add(hostTenant);
  if (tenants.size === 0) tenants.add(resolveTenantSlug() || 'main');
  tenants.forEach((tenant) => {
    storeSessionToken(token, { tenant, portal, sessionOnly });
  });
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantSlug, setTenantSlug] = useState(resolveTenantSlug());
  const profileRevision = useRef(0);
  const verifiedSession = useRef(null);

  useEffect(() => {
    const resolved = resolveTenantSlug();
    if (resolved) setTenantSlug(resolved);
    checkAuth();
  }, []);

  const checkAuth = async (requestOptions) => {
    const revision = profileRevision.current;
    let attemptedToken = null;
    try {
      const token = getStoredSessionToken();
      attemptedToken = token;
      if (!token) { verifiedSession.current = null; setUser(null); setLoading(false); return null; }
      if (isLocalDemoHost() && token === LOCAL_DEMO_TOKEN) {
        verifiedSession.current = token;
        setTenantSlug('demo');
        setUser(stampPlayerRewardsBootstrap(localDemoUser));
        setLoading(false);
        return { user: localDemoUser };
      }
      if (isLocalDemoHost() && token === LOCAL_PLAYER_DEMO_TOKEN) {
        verifiedSession.current = token;
        setTenantSlug('demo');
        setUser(stampPlayerRewardsBootstrap(localPlayerDemoUser));
        setLoading(false);
        return { user: localPlayerDemoUser };
      }
      const res = await (requestOptions ? api.get('/api/auth/me', requestOptions) : api.get('/api/auth/me'));
      const activeToken = getStoredSessionToken({ allowLegacyFallback: true });
      // Ignore a response for a session that was replaced while /auth/me was
      // in flight (for example, a signup completing after an old probe began).
      if (activeToken !== token) return;
      const currentTenant = resolveTenantSlug();
      if (!sessionMatchesCurrentTenant(res.data, currentTenant)) {
        verifiedSession.current = null;
        clearStoredSessionToken({ token });
        setUser(null);
        setLoading(false);
        return;
      }
      if (token) {
        storeSessionForTenantAliases(token, {
          canonicalTenant: res.data?.tenant_slug || tenantSlug,
          currentTenant,
          portal: currentPortalFromPath(),
        });
      }
      if (res.data?.tenant_slug) setTenantSlug(res.data.tenant_slug);
      const authUser = await hydratePhoneRewardBootstrap(res.data);
      // Hydration may outlive logout, signup or a replacement login.
      if (getStoredSessionToken() !== token || revision !== profileRevision.current) return null;
      const verifiedUser = stampPlayerRewardsBootstrap(authUser);
      verifiedSession.current = token;
      setUser(verifiedUser);
      return { user: verifiedUser };
    } catch (error) {
      const activeToken = getStoredSessionToken({ allowLegacyFallback: true });
      // A disconnected player is not an expired session. The connection gate
      // hides the lobby until a fresh authenticated check succeeds.
      if (isPlayerLobbyPath(window.location.pathname) && isTransportFailure(error)) return null;
      // Never interpret a tokenless/stale probe as permission to clear a
      // session which appeared while that probe was running.
      if (attemptedToken) clearStoredSessionToken({ token: attemptedToken });
      if (activeToken && activeToken !== attemptedToken) return;
      verifiedSession.current = null;
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (identifier, password, tenant = null, portal = null, options = {}) => {
    const loginPortal = portal || currentPortalFromPath();
    verifiedSession.current = null;
    clearStoredSessionToken({ tenant: tenant || tenantSlug, portal: loginPortal });
    setUser(null);
    if (
      isLocalDemoHost()
      && String(identifier || '').trim().toLowerCase() === LOCAL_DEMO_EMAIL
      && [LOCAL_DEMO_PASSWORD, LOCAL_DEMO_PASSWORD_SHORT].includes(password)
    ) {
      setDemoSessionToken(LOCAL_DEMO_TOKEN);
      verifiedSession.current = LOCAL_DEMO_TOKEN;
      setTenantSlug('demo');
      setUser(stampPlayerRewardsBootstrap(localDemoUser));
      return { token: LOCAL_DEMO_TOKEN, user: localDemoUser };
    }
    if (
      isLocalDemoHost()
      && String(identifier || '').trim().toLowerCase() === LOCAL_PLAYER_DEMO_EMAIL
      && [LOCAL_DEMO_PASSWORD, LOCAL_DEMO_PASSWORD_SHORT].includes(password)
    ) {
      setDemoSessionToken(LOCAL_PLAYER_DEMO_TOKEN);
      verifiedSession.current = LOCAL_PLAYER_DEMO_TOKEN;
      setTenantSlug('demo');
      setUser(stampPlayerRewardsBootstrap(localPlayerDemoUser));
      return { token: LOCAL_PLAYER_DEMO_TOKEN, user: localPlayerDemoUser };
    }
    const loginTenant = options.staffChat ? (options.tenantSlug || tenant || null) : (tenant || tenantSlug);
    const res = await api.post('/api/auth/login', {
      identifier,
      password,
      tenant_slug: loginTenant || undefined,
      portal: portal || undefined,
      staff_chat: Boolean(options.staffChat),
    });
    const token = res.data.token || res.data.session_id;
    if (!token || !res.data.user) throw new Error('Sign in did not return a session. Please try again.');
    const nextTenant = res.data.user?.tenant_slug || tenant || tenantSlug;
    if (token) {
      storeSessionForTenantAliases(token, {
        canonicalTenant: nextTenant,
        currentTenant: tenant || resolveTenantSlug() || tenantSlug,
        portal: loginPortal,
      });
    }
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    const authUser = await hydratePhoneRewardBootstrap(res.data.user);
    if (getStoredSessionToken({ portal: loginPortal }) !== token) throw new Error('Sign in was replaced. Please try again.');
    verifiedSession.current = token;
    setUser(stampPlayerRewardsBootstrap(authUser));
    return { ...res.data, user: authUser };
  };

  const register = async (email, password, fullName, username = null, referralCode = null, tenant = null, verificationCode = null) => {
    verifiedSession.current = null;
    clearStoredSessionToken({ tenant: tenant || tenantSlug, portal: 'player' });
    setUser(null);
    const res = await api.post('/api/auth/signup', {
      email,
      password,
      full_name: fullName || undefined,
      username: username || undefined,
      referral_code: referralCode || undefined,
      tenant_slug: tenant || tenantSlug || undefined,
      email_verification_code: verificationCode || undefined,
    });
    const token = res.data.token || res.data.session_id;
    if (!token || !res.data.user) throw new Error('Signup did not return a session. Please sign in again.');
    const nextTenant = res.data.user?.tenant_slug || tenant || tenantSlug;
    if (token) {
      storeSessionForTenantAliases(token, {
        canonicalTenant: nextTenant,
        currentTenant: tenant || resolveTenantSlug() || tenantSlug,
        portal: 'player',
      });
    }
    if (res.data.user?.tenant_slug) setTenantSlug(res.data.user.tenant_slug);
    const authUser = await hydratePhoneRewardBootstrap(res.data.user);
    if (getStoredSessionToken({ portal: 'player' }) !== token) throw new Error('Signup session was replaced. Please sign in again.');
    verifiedSession.current = token;
    setUser(stampPlayerRewardsBootstrap(authUser));
    return { ...res.data, user: authUser };
  };

  const logout = async ({ localOnly = false } = {}) => {
    const token = getStoredSessionToken();
    if (!localOnly) { try { await api.post('/api/auth/logout'); } catch {} }
    profileRevision.current += 1;
    clearStoredSessionToken({ token });
    verifiedSession.current = null;
    setUser(null);
  };

  const updateProfile = async (updates) => {
    const token = getStoredSessionToken();
    try {
      if (!token) {
        throw Object.assign(new Error('Please sign in again to finish setting your name.'), { response: { status: 401 } });
      }
      if (verifiedSession.current !== token) {
        // Another tab replaced storage while this old account's form stayed
        // open. Refresh identity, but never apply its draft to the new account.
        await checkAuth();
        throw Object.assign(new Error('Your session changed. Please check your account and enter your name again.'), { code: 'SESSION_CHANGED' });
      }
      const response = await api.patch('/api/auth/profile', updates, { expectedSessionToken: token });
      if (getStoredSessionToken() !== token) {
        throw new Error('Your session changed. Please try again.');
      }
      if (!response.data?.id || response.data.id !== user?.id) {
        throw new Error('Could not verify the saved profile. Please refresh and try again.');
      }
      // PATCH returns the saved profile. Do not leave the onboarding gate
      // open while a second /auth/me request refreshes unrelated balances.
      profileRevision.current += 1;
      setUser(previous => previous && previous.id === response.data.id
        ? stampPlayerRewardsBootstrap({ ...previous, ...response.data }) : previous);
      return response.data;
    } catch (error) {
      if (error?.response?.status === 401) {
        const current = getStoredSessionToken();
        if (token) clearStoredSessionToken({ token });
        if (!current || current === token) { verifiedSession.current = null; setUser(null); }
      }
      throw error;
    }
  };

  const exchangeSession = async (sessionId) => {
    if (!sessionId) throw new Error('No session found');
    storeSessionForTenantAliases(sessionId, {
      currentTenant: resolveTenantSlug(),
      portal: currentPortalFromPath(),
    });
    const result = await checkAuth();
    if (!result?.user) throw new Error('Please sign in again.');
    // A callback starts in the player URL scope, but staff must retain their
    // verified session when the callback enters the admin/staff portal.
    storeSessionForTenantAliases(sessionId, {
      canonicalTenant: result.user.tenant_slug,
      currentTenant: resolveTenantSlug(),
      portal: authPortalForUser(result.user),
    });
    return result;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      exchangeSession, checkAuth, updateProfile, tenantSlug, setTenantSlug
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
